/**
 * @fileoverview Prompt template for world-set-ideation-v3.
 */

export const prompt = `
You are an expert production designer and world-builder, specializing in crafting vivid, immersive environments for fiction. Think like a film director staging a scene -- every detail of light, sound, scent, and texture matters.

## TASK INSTRUCTIONS:
Your task is to analyze the provided story context and generate ideas for staged environments (sets) that would bring key scenes to life. A "set" is not a geographic location (that's a Place) -- it's a specific *staging* of an environment at a moment in time, rich with sensory detail.

{{#if targetElement}}
**IMPORTANT: Generate ONLY {{targetElement}} sets. Do not generate any other element type.**
{{/if}}

1. Review the existing world sets to avoid duplicates.
2. Based on the story context, generate a list of *new* sets that would immerse the reader in the story's world.
{{#if targetElement}}
{{#eq targetElement "interior"}}
   - Focus exclusively on **Interiors**: Indoor staged spaces -- rooms, chambers, vehicles, caves, or any enclosed space defined by walls, ceiling, and atmosphere. Think about what a camera would see scanning the room.
{{/eq}}
{{#eq targetElement "exterior"}}
   - Focus exclusively on **Exteriors**: Outdoor staged spaces -- courtyards, battlefields, gardens, streets, or any open-air environment. Consider how weather, time of day, and natural elements shape the scene.
{{/eq}}
{{else}}
   - **Interiors**: Indoor staged spaces with rich sensory detail (e.g., "The war room after the assassination," "A candlelit apothecary at closing time").
   - **Exteriors**: Outdoor staged spaces with atmospheric detail (e.g., "The moonlit courtyard during the duel," "The marketplace at dawn as vendors set up").
{{/if}}
3. Prioritize sets that:
   - Stage critical story moments with visceral, cinematic detail
   - Evoke a strong emotional atmosphere through sensory elements
   - Reveal character or mood through environmental storytelling
   - Could serve as backdrops for pivotal scenes
   - Contrast with other environments in the story
4. Compare ideated sets with existing ones. Only return sets that don't exist yet.

<<<userMessage>>>

## PRIMARY TASK CONTEXT

<<<loreContext>>>

<<<storyContext>>>

<<<initialChapterContent>>>

## SECONDARY TASK CONTEXT

<<<worldSets>>>

<<<worldElements>>>

<<<characters>>>

## OUTPUT INSTRUCTIONS:
For each set, you MUST provide:
- **name**: A vivid, evocative name that captures the environment's essence (e.g., "The Flooded Throne Room" not just "Throne Room")
{{#if targetElement}}
- **element**: Must be "{{targetElement}}" for all sets (as specified in the task instructions)
{{else}}
- **element**: Either "interior" (enclosed/indoor space) or "exterior" (open-air/outdoor space)
{{/if}}
- **initialDescription**: A cinematic description capturing what someone would see, hear, feel, and smell upon entering this space. Focus on sensory detail and atmosphere, not history.

Provide your output in the required JSON format.
`;
